import { matchPath } from 'react-router-dom';

/**
 * Where a user in the unified shell may still meet the legacy chrome: the dark `BouToolHeader` or
 * the 247px `BouPageWithFrame` (PLT-6596, "Guard before default-on").
 *
 * The rule is an allowlist on purpose. The shell reaches views one by one, so a route nobody has
 * decided about must count as a leak: an "everything except" rule would let the next route added
 * bring the old shell back without anyone noticing. Two consumers read these lists, and both treat
 * a path that is on neither as a leak:
 *
 * - `useReportLegacyShellLeak`, which `BouToolHeader` and `BouPageWithFrame` call, so a leak shows
 *   up in the console of whoever opens the page outside production.
 * - `legacy-shell-guard.spec.ts`, which walks the route table and fails CI when a route is added
 *   without saying which shell it renders in.
 *
 * Adding an entry is a product decision, not a fix. Say who decided, and where.
 */
export interface LegacyShellAllowlistEntry {
  /** A react-router pattern, matched against the whole pathname. */
  pattern: string;
  reason: string;
}

/** Customer-facing routes that stay outside the unified frame by decision. */
export const LEGACY_FRAME_ALLOWLIST: readonly LegacyShellAllowlistEntry[] = [
  {
    pattern: '/product/:id/*',
    reason: 'Product pages and how users navigate to them are a post-M2 research topic (PLT-6959).',
  },
  {
    pattern: '/user/:username/*',
    reason: 'Account settings keep their own frame, by Janne’s call on 29 Sep (PLT-6596, PLT-6737).',
  },
  {
    pattern: '/login',
    reason:
      'The sign-in page. A user who has just signed in sees its empty `BouPageWithFrame` for the moment ' +
      'before the redirect away (`login-view/index.tsx`), so the frame is expected there, not a leak.',
  },
];

/**
 * App-wide states that replace every route with a legacy-framed page, so no path could express
 * them. Read by `useReportLegacyShellLeak`, keyed by the state it checks.
 */
export const LEGACY_FRAME_ALLOWED_STATES = {
  fullMaintenance:
    'Full maintenance mode: `RoutesMaybe` renders `MaintenanceView`, a `BouPageWithFrame` notice, on every ' +
    'route while the platform is down, and there is no shell to be in.',
} as const;

/**
 * Views only an Upright team account can open. The unified shell is a customer rollout, and a team
 * user is in it only while the `internalAdmin.unifiedShell` View setting is on, so these are out of
 * scope rather than waiting for a design.
 */
export const TEAM_ONLY_LEGACY_ROUTES: readonly LegacyShellAllowlistEntry[] = [
  { pattern: '/internal/*', reason: 'Internal admin views, gated by `useIsTeam` in the route tree.' },
  ...['company', 'company-group', 'portfolio'].flatMap(entityType => [
    { pattern: `/${entityType}/:id/internal/*`, reason: 'The entity’s internal view, team-only.' },
    { pattern: `/${entityType}/:id/:name/internal/*`, reason: 'The entity’s internal view, team-only.' },
  ]),
];

function matchesAny(entries: readonly LegacyShellAllowlistEntry[], pathname: string): boolean {
  return entries.some(entry => matchPath(entry.pattern, pathname) !== null);
}

export function isLegacyFrameAllowlistedPath(pathname: string): boolean {
  return matchesAny(LEGACY_FRAME_ALLOWLIST, pathname);
}

export function isTeamOnlyLegacyPath(pathname: string): boolean {
  return matchesAny(TEAM_ONLY_LEGACY_ROUTES, pathname);
}

/** Whether the legacy chrome on this path is expected for a user in the unified shell. */
export function isLegacyShellAllowedPath(pathname: string): boolean {
  return isLegacyFrameAllowlistedPath(pathname) || isTeamOnlyLegacyPath(pathname);
}
